import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  DialogTitle,
  DialogActions,
  Container,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Add, Close, Description, Visibility, Edit } from "@mui/icons-material";
import axios from "axios";
import { useToast } from "../../components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { Loading } from "../../components/Loading";
import { uploadFile } from "../../utils/UploadFile";
import { LocalUser } from "../../utils/UploadFile";
import { fetchDocuments } from "../../utils/FetchDocuments";
import { DocumentUploadSection } from "../../components/DocumentUploadSection";
import { UploadedFile } from "../../types/loan";
import NavigationHeader from "../../components/NavigationHeader";
import { commonDateFieldProps, commonTextFieldProps } from "../../utils/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCurrency, formatDate } from "../../utils/utils";
import { useFormik } from "formik";
import * as Yup from "yup";

interface Insurance {
  id: string;
  insuranceType: string;
  insuranceProvider: string;
  insurancePolicyNumber: string;
  insuranceStartDate: string;
  insuranceEndDate: string;
  insurancePremium: number;
  isActive: boolean;
  documents?: UploadedFile[];
}

const insuranceValidationSchema = Yup.object().shape({
  insuranceType: Yup.string()
    .required("Insurance type is required")
    .notOneOf(["Select"], "Please select a valid insurance type"),
  insuranceProvider: Yup.string()
    .required("Insurance provider is required")
    .max(100, "Insurance provider must not exceed 100 characters"),
  insurancePolicyNumber: Yup.string()
    .required("Policy number is required")
    .max(50, "Policy number must not exceed 50 characters"),
  insuranceStartDate: Yup.string().required("Insurance start date is required"),
  insuranceEndDate: Yup.string()
    .required("Insurance end date is required")
    .test(
      "end-date-after-start",
      "End date must be after start date",
      function (value) {
        const { insuranceStartDate } = this.parent;
        if (insuranceStartDate && value) {
          return new Date(value) > new Date(insuranceStartDate);
        }
        return true;
      }
    ),
  insurancePremium: Yup.string()
    .required("Insurance premium is required")
    .matches(/^[0-9]*\.?[0-9]+$/, "Premium must be a valid decimal number")
    .test(
      "min",
      "Premium must be greater than 0",
      (value) => Number(value) > 0
    ),
});

const InsuranceTable = ({
  insurances,
  handleEditInsurance,
  handlePreview,
}: {
  insurances: Insurance[];
  handleEditInsurance: (insurance: Insurance) => void;
  handlePreview: (url: string) => void;
}) => {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tableCellStyles}>Insurance Type</TableCell>
            <TableCell sx={tableCellStyles}>Provider</TableCell>
            <TableCell sx={tableCellStyles}>Policy Number</TableCell>
            <TableCell sx={tableCellStyles}>Start Date</TableCell>
            <TableCell sx={tableCellStyles}>End Date</TableCell>
            <TableCell sx={tableCellStyles}>Premium</TableCell>
            <TableCell sx={tableCellStyles}>Status</TableCell>
            <TableCell sx={tableCellStyles}>Document</TableCell>
            <TableCell sx={tableCellStyles}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {insurances?.map((insurance) => (
            <TableRow key={insurance.id}>
              <TableCell sx={tableCellStyles}>
                {insurance.insuranceType}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {insurance.insuranceProvider}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {insurance.insurancePolicyNumber}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {formatDate(insurance.insuranceStartDate)}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {formatDate(insurance.insuranceEndDate)}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {formatCurrency(insurance.insurancePremium)}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                <Chip
                  label={insurance.isActive ? "Active" : "Expired"}
                  color={insurance.isActive ? "success" : "error"}
                  size="small"
                  sx={{ fontSize: "0.7rem" }}
                />
              </TableCell>
              <TableCell sx={tableCellStyles}>
                {insurance.documents && insurance.documents.length > 0 ? (
                  <>
                    {insurance.documents.map((document) => (
                      <Box
                        key={document.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Description
                          color="primary"
                          sx={{ width: "15px", height: "15px" }}
                        />
                        <Typography variant="body2" sx={tableCellStyles}>
                          {document.fileName}
                        </Typography>
                        <IconButton
                          onClick={() => handlePreview(document.presignedUrl)}
                          size="small"
                          sx={{ color: "primary.main", ml: "auto" }}
                        >
                          <Visibility sx={{ width: "15px", height: "15px" }} />
                        </IconButton>
                      </Box>
                    ))}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={tableCellStyles}
                  >
                    No document
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={tableCellStyles}>
                <IconButton
                  onClick={() => handleEditInsurance(insurance)}
                  sx={{ color: "primary.main" }}
                >
                  <Edit sx={{ width: "15px", height: "15px" }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {(!insurances || !insurances?.length) && (
            <TableRow>
              <TableCell colSpan={9} align="center">
                No insurances found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const VehicleInsurance = () => {
  const { vehicleId } = useParams();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user, loading } = useFirebaseAuth();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : "";
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingInsurance, setIsSubmittingInsurance] = useState(false);
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [activeInsurances, setActiveInsurances] = useState<Insurance[]>([]);
  const [expiredInsurances, setExpiredInsurances] = useState<Insurance[]>([]);
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(
    null
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedFile[]>(
    []
  );
  const [isUploading, setIsUploading] = useState(false);

  const formik = useFormik({
    initialValues: {
      insuranceType: "Select",
      insuranceProvider: "",
      insurancePolicyNumber: "",
      insuranceStartDate: "",
      insuranceEndDate: "",
      insurancePremium: "",
    },
    validationSchema: insuranceValidationSchema,
    onSubmit: async (values) => {
      await handleInsuranceSubmit(values);
    },
  });

  useEffect(() => {
    if (user && vehicleId && !loading) {
      fetchInsurances();
    }
  }, [user, vehicleId, loading]);

  const fetchInsurances = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}vehicle-data/insurance/vehicle/${vehicleId}`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        const insurancesData = response?.data?.data;
        // Fetch documents for each insurance
        const insurancesWithDocs = await Promise.all(
          insurancesData.map(async (insurance: Insurance) => {
            const docs = await fetchDocuments({
              documentContext: "vehicle",
              documentTypeId: insurance.id,
              documentType: "vehicle-insurance",
            });
            return {
              ...insurance,
              documents: docs.map((doc) => ({
                id: doc.id,
                fileName: doc.fileName,
                presignedUrl: doc.presignedUrl,
              })),
            };
          })
        );
        setActiveInsurances(
          insurancesWithDocs.filter((insurance) => insurance.isActive)
        );
        setExpiredInsurances(
          insurancesWithDocs.filter((insurance) => !insurance.isActive)
        );
      } else {
        showToast("Failed to fetch insurances", "error");
      }
    } catch (error) {
      console.error("Error fetching insurances:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const handleEditInsurance = (insurance: Insurance) => {
    setSelectedInsurance(insurance);
    formik.setValues({
      insuranceType: insurance.insuranceType || "",
      insuranceProvider: insurance.insuranceProvider || "",
      insurancePolicyNumber: insurance.insurancePolicyNumber || "",
      insuranceStartDate:
        new Date(insurance.insuranceStartDate).toISOString().split("T")[0] ||
        "",
      insuranceEndDate:
        new Date(insurance.insuranceEndDate).toISOString().split("T")[0] || "",
      insurancePremium: insurance.insurancePremium?.toString() || "",
    });
    if (insurance.documents && insurance.documents.length > 0) {
      setUploadedDocuments(insurance.documents);
    }
    setIsDialogOpen(true);
  };

  const handleInsuranceSubmit = async (values: typeof formik.values) => {
    setIsSubmittingInsurance(true);
    try {
      const enrichedInsuranceForm = {
        ...values,
        vehicleId: vehicleId || "",
        isActive: new Date(values.insuranceEndDate) > new Date() ? true : false,
        insurancePremium: parseFloat(values.insurancePremium),
      };

      let response;
      if (selectedInsurance) {
        // Update existing insurance
        response = await axios.put(
          `${process.env.REACT_APP_API_HOST}vehicle-data/insurance/${selectedInsurance.id}`,
          enrichedInsuranceForm,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      } else {
        // Create new insurance
        response = await axios.post(
          `${process.env.REACT_APP_API_HOST}vehicle-data/insurance`,
          enrichedInsuranceForm,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
      }

      if (response?.data?.isSuccess) {
        if (selectedFiles.length > 0) {
          let promises = [];
          for (const file of selectedFiles) {
            promises.push(
              uploadFile({
                file,
                documentType: "vehicle-insurance",
                documentContext: "vehicle",
                description: values.insuranceType,
                documentTypeId: selectedInsurance
                  ? selectedInsurance.id
                  : response.data.data.id,
                companyId,
              })
            );
          }
          await Promise.all(promises);
        }
        showToast(
          `Insurance ${selectedInsurance ? "updated" : "added"} successfully`,
          "success"
        );
        setIsDialogOpen(false);
        setSelectedFiles([]);
        setSelectedInsurance(null);
        fetchInsurances();
      } else {
        showToast(
          `Failed to ${selectedInsurance ? "update" : "add"} insurance`,
          "error"
        );
      }
    } catch (error) {
    } finally {
      setIsSubmittingInsurance(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedFiles([]);
    setSelectedInsurance(null);
    formik.resetForm();
    setUploadedDocuments([]);
  };

  if (loading || isLoading) {
    return <Loading message={`Loading insurance...`} fullScreen={true} />;
  }

  if (isSubmittingInsurance) {
    return (
      <Loading
        message={`${
          selectedInsurance ? "Updating" : "Submitting"
        } insurance...`}
        fullScreen={true}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        title="Vehicle Insurance"
        breadcrumbs={[
          { label: "Vehicles", path: "/vehicles" },
          { label: "Vehicle Details", path: `/vehicles/${vehicleId}` },
          { label: "Vehicle Insurance" },
        ]}
        button={{
          label: "Add New Insurance",
          onClick: () => setIsDialogOpen(true),
          icon: <Add sx={{ width: "15px", height: "15px" }} />,
          variant: "contained",
          color: "primary",
        }}
      />
      <Accordion sx={{ mb: 2 }} defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          Active Insurances
        </AccordionSummary>
        <AccordionDetails>
          <InsuranceTable
            insurances={activeInsurances}
            handleEditInsurance={handleEditInsurance}
            handlePreview={handlePreview}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          Expired Insurances
        </AccordionSummary>
        <AccordionDetails>
          <InsuranceTable
            insurances={expiredInsurances}
            handleEditInsurance={handleEditInsurance}
            handlePreview={handlePreview}
          />
        </AccordionDetails>
      </Accordion>

      {/* Insurance Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ fontSize: "1rem" }}
          >
            {selectedInsurance ? "Edit Insurance" : "Add New Insurance"}
            <IconButton onClick={handleCloseDialog} size="small" title="Close">
              <Close sx={{ width: "20px", height: "20px" }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Insurance Type"
                name="insuranceType"
                value={formik.values.insuranceType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.insuranceType &&
                  Boolean(formik.errors.insuranceType)
                }
                helperText={
                  formik.touched.insuranceType && formik.errors.insuranceType
                }
                fullWidth
                disabled={!!selectedInsurance}
                {...commonTextFieldProps}
              >
                <MenuItem value="Select" disabled sx={{ fontSize: "0.75rem" }}>
                  Select
                </MenuItem>
                <MenuItem value="Third Party" sx={{ fontSize: "0.75rem" }}>
                  Third Party
                </MenuItem>
                <MenuItem value="Own Damage" sx={{ fontSize: "0.75rem" }}>
                  Own Damage
                </MenuItem>
                <MenuItem value="Comprehensive" sx={{ fontSize: "0.75rem" }}>
                  Comprehensive
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Insurance Provider"
                name="insuranceProvider"
                value={formik.values.insuranceProvider}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.insuranceProvider &&
                  Boolean(formik.errors.insuranceProvider)
                }
                helperText={
                  formik.touched.insuranceProvider &&
                  formik.errors.insuranceProvider
                }
                fullWidth
                required
                {...commonTextFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Policy Number"
                name="insurancePolicyNumber"
                value={formik.values.insurancePolicyNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.insurancePolicyNumber &&
                  Boolean(formik.errors.insurancePolicyNumber)
                }
                helperText={
                  formik.touched.insurancePolicyNumber &&
                  formik.errors.insurancePolicyNumber
                }
                fullWidth
                required
                inputProps={{
                  maxLength: 50,
                }}
                {...commonTextFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                label="Insurance Premium"
                name="insurancePremium"
                value={formik.values.insurancePremium}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.insurancePremium &&
                  Boolean(formik.errors.insurancePremium)
                }
                helperText={
                  formik.touched.insurancePremium &&
                  formik.errors.insurancePremium
                }
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ fontSize: "0.8rem", marginRight: "5px" }}>
                      ₹
                    </Typography>
                  ),
                }}
                {...commonTextFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="Insurance Start Date"
                name="insuranceStartDate"
                value={formik.values.insuranceStartDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.insuranceStartDate &&
                  Boolean(formik.errors.insuranceStartDate)
                }
                helperText={
                  formik.touched.insuranceStartDate &&
                  formik.errors.insuranceStartDate
                }
                fullWidth
                required
                {...commonDateFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="Insurance End Date"
                name="insuranceEndDate"
                value={formik.values.insuranceEndDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.insuranceEndDate &&
                  Boolean(formik.errors.insuranceEndDate)
                }
                helperText={
                  formik.touched.insuranceEndDate &&
                  formik.errors.insuranceEndDate
                }
                fullWidth
                required
                {...commonDateFieldProps}
              />
            </Grid>
            <Grid item xs={12}>
              <DocumentUploadSection
                title={`${
                  selectedInsurance ? "Update" : "Upload"
                } Insurance Document`}
                selectedFiles={selectedFiles}
                uploadedFiles={uploadedDocuments}
                onFileSelect={(files: File[]) => {
                  setSelectedFiles(files);
                }}
                onFileRemove={(index: number) =>
                  setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
                }
                onPreview={handlePreview}
                isLoading={isUploading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={isSubmittingInsurance}
            sx={{ fontSize: "0.8rem", height: "30px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => formik.handleSubmit()}
            disabled={isSubmittingInsurance}
            sx={{ fontSize: "0.8rem", height: "30px" }}
          >
            {isSubmittingInsurance
              ? `${selectedInsurance ? "Updating" : "Submitting"}...`
              : selectedInsurance
              ? "Update"
              : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VehicleInsurance;

const tableCellStyles = {
  fontSize: "0.7rem",
};
